import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

import { BaseListFooter } from '../base/BaseListFooter';
import { cn } from '../base/utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from '../primitives';
import { useMonkeysLocaleMessages } from '../../provider/context';
import type { DataExplorerCollectionFooterProps } from './types';

export function DataExplorerCollectionFooter({ selectedCount, loadedStart, loadedEnd, total, currentPage = 1, pageSize = Math.max(1, loadedEnd || 1), pageSizeOptions = [], loading, selectedLabel, loadedLabel, pageSizeLabel, pageText, tone = 'default', className, classNames, onJump, onJumpToPage, onPageSizeChange }: DataExplorerCollectionFooterProps) {
  const messages = useMonkeysLocaleMessages().pagination;
  const hasRange = typeof loadedStart === 'number' && loadedStart > 0 && loadedEnd >= loadedStart;
  const resolvedLoadedLabel = loadedLabel ?? (hasRange ? messages.range({ start: loadedStart, end: loadedEnd, total }) : messages.range({ start: 0, end: loadedEnd, total }));
  const [isEditingJump, setIsEditingJump] = useState(false);
  const [jumpInput, setJumpInput] = useState(String(currentPage));
  const jumpInputRef = useRef<HTMLInputElement | null>(null);
  const maxPage = total > 0 ? Math.max(1, Math.ceil(total / Math.max(1, pageSize))) : 1;

  useEffect(() => {
    if (!isEditingJump) setJumpInput(String(currentPage));
  }, [currentPage, isEditingJump]);

  useEffect(() => {
    if (!isEditingJump) return;
    const timer = window.setTimeout(() => {
      jumpInputRef.current?.focus();
      jumpInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isEditingJump]);

  if (tone === 'hard-edge') {
    const confirmJump = () => {
      const parsed = Math.floor(Number(jumpInput));
      if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
        setJumpInput(String(currentPage));
        setIsEditingJump(false);
        return;
      }
      const target = Math.min(maxPage, Math.max(1, parsed));
      setIsEditingJump(false);
      if (target !== currentPage) onJumpToPage?.(target);
    };
    return (
      <div
        className={cn('flex items-center border-t bg-background px-4 py-3', classNames?.root, className)}
        data-monkeys-component="collection-footer"
        data-tone="hard-edge"
      >
        <div className={cn('flex min-w-0 items-center gap-1 whitespace-nowrap text-sm text-muted-foreground', classNames?.summary)}>
          {selectedCount !== undefined ? <span>{selectedLabel ?? messages.selected({ count: selectedCount })} ·</span> : null}
          {onJumpToPage && total > 0 ? (
            isEditingJump ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{messages.jumpToPage}</span>
                <Input
                  ref={jumpInputRef}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={maxPage}
                  value={jumpInput}
                  onChange={(event) => setJumpInput(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      setIsEditingJump(false);
                      setJumpInput(String(currentPage));
                    }
                  }}
                  onBlur={confirmJump}
                  className="h-7 w-20 px-2 text-xs"
                  aria-label={messages.jumpToPage}
                />
                <span className="text-xs text-muted-foreground">/ {maxPage} {messages.pages}</span>
              </div>
            ) : (
              <button
                type="button"
                className={cn('inline-flex items-center text-muted-foreground underline-offset-4 hover:text-foreground hover:underline', classNames?.jumpButton)}
                onClick={() => {
                  setJumpInput(String(currentPage));
                  setIsEditingJump(true);
                  onJump?.();
                }}
                title={messages.clickToJump}
              >
                {resolvedLoadedLabel}{pageText ? ` (${pageText})` : null}
                {loading ? <Loader2 aria-hidden="true" className="ml-1 inline-block h-3 w-3 animate-spin" /> : null}
              </button>
            )
          ) : (
            <span className="inline-flex items-center">
              {resolvedLoadedLabel}{pageText ? ` (${pageText})` : null}
              {loading ? <Loader2 aria-hidden="true" className="ml-1 h-3 w-3 animate-spin" /> : null}
            </span>
          )}
        </div>
        {onPageSizeChange && pageSizeOptions.length > 0 ? (
          <div className={cn('ml-auto', classNames?.pageSizeControl)}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn('h-8 gap-1 px-2', classNames?.pageSizeButton)}
                  aria-label={typeof pageSizeLabel === 'string' ? pageSizeLabel : messages.perLoad}
                >
                  {pageSize}
                  <ChevronDown aria-hidden="true" className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuLabel>{pageSizeLabel ?? messages.perLoad}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    const next = Number(value);
                    if (Number.isFinite(next)) onPageSizeChange(next);
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <DropdownMenuRadioItem key={size} value={String(size)}>{size}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
    );
  }
  return <BaseListFooter total={total} loadedStart={loadedStart} loadedEnd={loadedEnd} selectedCount={selectedCount} page={currentPage} pageSize={pageSize} pageSizeOptions={pageSizeOptions} loading={loading} totalLabel={<>{resolvedLoadedLabel} {pageText ? `(${pageText})` : null}</>} selectedLabel={selectedLabel} pageSizeLabel={pageSizeLabel} className={className} classNames={{ root: classNames?.root, summary: classNames?.summary, pageSize: classNames?.pageSizeControl }} onSummaryClick={onJump} onPageSizeChange={onPageSizeChange} />;
}
